/**
 * Flip a Basecamp ingestion thread from legacy polling/refinement UX to normal chat-stream mode.
 */

import type { Message, DocumentReference } from '@/components/layout/chat-pane.types';
import {
  type BasecampIngestionCompleteDetail,
  getIngestionCompleteDetail,
} from '@/lib/basecamp-ingestion-events';
import { resolveDocumentRowId } from '@/lib/api';

export type FlipIngestionConversationParams = {
  stopPolling: () => void;
  setCurrentConversationId: (id: string | null) => void;
  setStreamingConversationId: (id: string | null) => void;
  setStreamingMessages: (messages: Message[]) => void;
  setLegacyMessages: (messages: Message[]) => void;
  setThinkingModeActive?: (active: boolean) => void;
  conversationId: string;
  conversation: {
    metadata?: Record<string, unknown>;
    messages?: unknown[];
    status?: string;
  };
  /** Optional hint from ingestion-complete dispatch (preferred over re-parsing messages). */
  filePathHint?: string;
  /** Open the ingested markdown in the main viewer (streaming chat context). */
  onActivateIngestedDocument?: (documentId: string) => void;
  /** When true, resolve metadata file path and open doc tab. Default false (sidebar/history reload). */
  openIngestedDocument?: boolean;
};

export function mapConversationMessagesForStreamingUi(raw: unknown[]): Message[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((msg: any) => msg?.role === 'user' || msg?.role === 'assistant')
    .map((msg: any) => ({
      role: msg.role as 'user' | 'assistant',
      content: msg.content ?? '',
      references: msg.references as DocumentReference[] | undefined,
      messageId: msg.id != null ? String(msg.id) : undefined,
      aborted: msg.metadata?.status === 'aborted',
      metadata: msg.metadata ?? undefined,
      timestamp: msg.created_at ? new Date(msg.created_at) : undefined,
      skill: msg.metadata?.skill ?? undefined,
      editOf: msg.edit_of != null ? String(msg.edit_of) : undefined,
    }));
}

/** True when an ingestion-mode conversation looks finished (ready for streaming follow-up). */
export function isIngestionThreadComplete(conv: {
  metadata?: Record<string, unknown>;
  status?: string;
  messages?: Array<{ content?: string; metadata?: Record<string, unknown> }>;
}): boolean {
  return (
    getIngestionCompleteDetail({
      metadata: conv.metadata,
      status: conv.status,
      messages: conv.messages,
    }) != null
  );
}

async function resolveIngestedDocumentId(params: {
  metadata: Record<string, unknown>;
  detail?: BasecampIngestionCompleteDetail | null;
}): Promise<string | null> {
  const path =
    params.detail?.filePath ??
    (typeof params.metadata.todoFile === 'string' ? params.metadata.todoFile : null) ??
    (typeof params.metadata.messageFile === 'string' ? params.metadata.messageFile : null);
  if (!path || typeof path !== 'string') return null;
  try {
    return await resolveDocumentRowId(path.trim());
  } catch {
    return null;
  }
}

/**
 * Stops ingestion polling, moves messages to streaming state, clears legacy buffers, activates ingested doc.
 */
export async function flipIngestionConversationToStreaming(
  p: FlipIngestionConversationParams,
): Promise<void> {
  p.stopPolling();

  const rawMessages = p.conversation.messages;
  const arr = Array.isArray(rawMessages) ? rawMessages : [];

  const detail = getIngestionCompleteDetail({
    id: p.conversationId,
    metadata: p.conversation.metadata,
    status: p.conversation.status,
    messages: arr as Parameters<typeof getIngestionCompleteDetail>[0]['messages'],
  });

  const meta = p.conversation.metadata ?? {};

  const mergedDetail: BasecampIngestionCompleteDetail | null =
    detail == null && p.filePathHint
      ? { conversationId: p.conversationId, filePath: p.filePathHint }
      : detail != null
        ? {
            ...detail,
            filePath: detail.filePath ?? p.filePathHint,
          }
        : null;

  const mapped = mapConversationMessagesForStreamingUi(arr);

  p.setThinkingModeActive?.(false);
  p.setCurrentConversationId(null);
  p.setLegacyMessages([]);
  p.setStreamingConversationId(p.conversationId);
  p.setStreamingMessages(mapped);

  if (p.openIngestedDocument && p.onActivateIngestedDocument) {
    const docId = await resolveIngestedDocumentId({
      metadata: meta,
      detail: mergedDetail,
    });
    if (docId) {
      p.onActivateIngestedDocument(docId);
    }
  }
}
